import { StatsContextInterface } from '../../stats/context.interface';
import { StatsDataPropertyInterface } from '../../stats/data/property.interface';
import { StatsObserver } from '../../stats/observer';
import { Snowplow2StatsTracker } from './tracker';
import { Snowplow2StatsDataProperty } from './data/property';
import { Snowplow2StatsAdapterSchemaContentImpression } from './adapter/schema-content-impression';
import { Snowplow2StatsAdapterSchemaContentLoad } from './adapter/schema-content-load';
import { Snowplow2StatsAdapterSchemaLeadClick } from './adapter/schema-lead-click';
import { Snowplow2StatsAdapterSchemaLeadSend } from './adapter/schema-lead-send';
import { StatsDataAgentInterface } from '../../stats/data/agent.interface';
import { Snowplow2StatsDataAgent } from './data/agent';
import { StatsDataBrokerInterface } from '../../stats/data/broker.interface';
import { Snowplow2StatsDataBroker } from './data/broker';
import { Snowplow2StatsAdapterSchemaContentView } from './adapter/schema-content-view';
import { Snowplow2StatsAdapterSchemaContentClick } from './adapter/schema-content-click';
import { Snowplow2StatsAdapterSchemaContentSave } from './adapter/schema-content-save';
import { Snowplow2StatsAdapterSchemaContentUnsave } from './adapter/schema-content-unsave';
import { Snowplow2StatsAdapterSchemaContentSearch } from './adapter/schema-content-search';
import { Snowplow2StatsDataSearch } from './data/search';
import { StatsDataUserInterface } from '../../stats/data/user.interface';
import { Snowplow2StatsDataTrackable } from './data/trackable';
import { Snowplow2StatsAdapterSchemaLayoutContext } from './adapter/schema-layout-context';
import { Snowplow2StatsAdapterSchemaReadmoreClick } from './adapter/schema-readmore-click';
import { Snowplow2StatsDataPageView } from './data/page-view';

export class Snowplow2StatsObserver extends StatsObserver {
  /**
   *
   * @param statsTracker
   */
  constructor(protected statsTracker: Snowplow2StatsTracker) {
    super(statsTracker);
  }

  /**
   * @inheritDoc
   */
  protected onPageView(context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataPageView(context);

    // Track converted data
    this.statsTracker.pageView(
      context.global.authentication.user ? String(context.global.authentication.user.id) : '',
      data
    );
  }

  /**
   * @inheritDoc
   */
  protected onPropertyPage(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentView().getData());

    // Track converted data
    this.statsTracker.propertyPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertySerp(context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataSearch(context);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentSearch().getData());

    // Track converted data
    this.statsTracker.propertySerp(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyImpression(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentImpression().getData());

    // Track converted data
    this.statsTracker.propertyImpression(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLoad(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentLoad().getData());

    // Track converted data
    this.statsTracker.propertyLoad(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLeadClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadClick(context).getData());

    // Track converted data
    this.statsTracker.propertyLeadClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLeadSend(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Since snowplow tracking for call-back lead performed on BE we no need to duplicate it in FE
    if (context.local.lead.medium === 'call_back') {
      return;
    }

    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadSend(context).getData());

    // Track converted data
    this.statsTracker.propertyLeadSend(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentClick(context).getData());

    // Track converted data
    this.statsTracker.propertyClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertySave(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentSave(context).getData());

    // Track converted data
    this.statsTracker.propertySave(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyUnsave(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentUnsave(context).getData());

    // Track converted data
    this.statsTracker.propertyUnsave(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentPage(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataAgent(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentView().getData());

    // Track converted data
    this.statsTracker.agentPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentSerp(context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataSearch(context);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentSearch().getData());

    // Track converted data
    this.statsTracker.agentSerp(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentImpression(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataAgent(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentImpression().getData());

    // Track converted data
    this.statsTracker.agentImpression(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentLoad(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataAgent(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentLoad().getData());

    // Track converted data
    this.statsTracker.agentLoad(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentLeadClick(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataAgent(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadClick(context).getData());

    // Track converted data
    this.statsTracker.agentLeadClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentLeadSend(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataAgent(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadSend(context).getData());

    // Track converted data
    this.statsTracker.agentLeadSend(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentClick(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataAgent(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentClick(context).getData());

    // Track converted data
    this.statsTracker.agentClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerPage(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataBroker(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentView().getData());

    // Track converted data
    this.statsTracker.brokerPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerSerp(context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataSearch(context);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentSearch().getData());

    // Track converted data
    this.statsTracker.brokerSerp(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerImpression(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataBroker(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentImpression().getData());

    // Track converted data
    this.statsTracker.brokerImpression(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerLoad(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataBroker(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentLoad().getData());

    // Track converted data
    this.statsTracker.brokerLoad(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerLeadClick(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataBroker(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadClick(context).getData());

    // Track converted data
    this.statsTracker.brokerLeadClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerLeadSend(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataBroker(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadSend(context).getData());

    // Track converted data
    this.statsTracker.brokerLeadSend(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerClick(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataBroker(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaContentClick(context).getData());

    // Track converted data
    this.statsTracker.brokerClick(data);
  }

  /**
   * Emit a user sign in
   *
   * @param rawData
   * @param context
   */
  protected onUserSignIn(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    // Track converted data
    this.statsTracker.userSignIn(String(rawData.id));
  }

  /**
   * Emit a user sign out
   *
   * @param rawData
   * @param context
   */
  protected onUserSignOut(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    // Track converted data
    this.statsTracker.userSignOut(String(rawData.id));
  }

  /**
   * Emit a user register
   *
   * @param rawData
   * @param context
   */
  protected onUserRegister(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    // Track converted data
    this.statsTracker.userRegister(String(rawData.id));
  }

  /**
   * Emit my account page view
   *
   * @param context
   */
  protected onMyAccountPage(context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataTrackable(context);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLayoutContext(context).getData());

    this.statsTracker.myAccountPage(data);
  }

  /**
   * Emitted property description read more click
   *
   * @param context
   */
  protected onPropertyReadmoreClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataTrackable(context);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaReadmoreClick(context).getData());

    this.statsTracker.propertyReadmoreClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLeadSendWhatsapp(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert raw data
    const data = new Snowplow2StatsDataProperty(context, rawData);

    data.setEventSchema(new Snowplow2StatsAdapterSchemaLeadSend(context).getData());

    // Track converted data
    this.statsTracker.propertyLeadSendWhatsapp(data);
  }
}
